import React from "react";
import { formatCurrency } from "../utils/formatCurrency";
import ParaplanningSelectField from "../components/ParaplanningSelectField";

import { ReactComponent as DropDownIcon } from "../../../assets/icons/paraplanningDashboard/ArrowDecrease.svg";
import { ReactComponent as DropUpIcon } from "../../../assets/icons/paraplanningDashboard/ArrowIncrease.svg";

const SummaryCard = ({
  label,
  value,
  helperText,
  variant = "default",
  changeValue,
}) => {
  const hasChange = changeValue !== undefined && changeValue !== null;
  const numericChange = Number(changeValue || 0);
  const isNegative = hasChange && numericChange < 0;
  const isPositive = hasChange && numericChange > 0;
  const TrendIcon = isNegative ? DropDownIcon : DropUpIcon;

  return (
    <article
      className={`ParaplanningSummaryCard ParaplanningSummaryCard--${variant}`}
    >
      <div className="ParaplanningSummaryCard__Label">{label}</div>

      <div className="ParaplanningSummaryCard__Value">{value}</div>

      {helperText && (
        <div
          className={[
            "ParaplanningSummaryCard__Helper",
            isPositive ? "ParaplanningSummaryCard__Helper--Up" : "",
            isNegative ? "ParaplanningSummaryCard__Helper--Down" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {hasChange && numericChange !== 0 && <TrendIcon />}
          <span>{helperText}</span>
        </div>
      )}
    </article>
  );
};

const formatChangeText = (change, label) => {
  const value = Number(change || 0);
  const directionText = value < 0 ? "down" : "up";

  return `${Math.abs(value)}% ${directionText} from ${label}`;
};

const ParaplanningSummaryCards = ({
  totals = {},
  summaryMode = "quarter",
  summaryOptions = [],
  selectedSummaryKey = "",
  onSummaryModeChange,
  onSummaryKeyChange,
  hideFilters = false,
  hideCards = false,
  extraCards = [],
}) => {
  const isQuarter = summaryMode === "quarter";

  const periodLabel =
    totals.periodLabel || (isQuarter ? "This Quarter" : "This Month");

  const previousPeriodLabel =
    totals.previousPeriodLabel || (isQuarter ? "last quarter" : "last month");

  const periodTotal = Number(totals.periodTotal || 0);
  const periodCaseCount = Number(totals.periodCaseCount || 0);
  const periodInProgressCount = Number(totals.periodInProgressCount || 0);

  const cards = [
    {
      id: "period-total",
      label: periodLabel,
      value: formatCurrency(periodTotal),
      helperText: formatChangeText(totals.periodChange, previousPeriodLabel),
      changeValue: totals.periodChange,
      variant: "blue",
    },
    {
      id: "completed-cases",
      label: `${periodLabel} Completed Cases`,
      value: periodCaseCount,
      helperText: formatChangeText(
        totals.periodCaseCountChange,
        previousPeriodLabel,
      ),
      changeValue: totals.periodCaseCountChange,
      variant: "green",
    },
    {
      id: "in-progress-cases",
      label: "In Progress Cases",
      value: periodInProgressCount,
      helperText: formatChangeText(
        totals.periodInProgressCountChange,
        previousPeriodLabel,
      ),
      changeValue: totals.periodInProgressCountChange,
      variant: "cream",
    },
    ...extraCards.filter(Boolean),
  ];

  return (
    <section className="ParaplanningSummary">
      {!hideFilters && (
        <div className="ParaplanningSummary__Filters">
          <div className="ParaplanningSummary__Toggle">
            <button
              type="button"
              className={
                !isQuarter
                  ? "ParaplanningSummary__ToggleButton ParaplanningSummary__ToggleButton--Active FP_Content"
                  : "ParaplanningSummary__ToggleButton FP_Content"
              }
              onClick={() => onSummaryModeChange("month")}
            >
              Month
            </button>

            <button
              type="button"
              className={
                isQuarter
                  ? "ParaplanningSummary__ToggleButton ParaplanningSummary__ToggleButton--Active FP_Content"
                  : "ParaplanningSummary__ToggleButton FP_Content"
              }
              onClick={() => onSummaryModeChange("quarter")}
            >
              Quarter
            </button>
          </div>

          <ParaplanningSelectField
            value={selectedSummaryKey}
            placeholder={isQuarter ? "Select quarter" : "Select month"}
            options={summaryOptions}
            clearable={false}
            searchable={false}
            onChange={onSummaryKeyChange}
          />
        </div>
      )}

      {!hideCards && (
        <div className="ParaplanningSummaryCards">
          {cards.map((card) => (
            <SummaryCard
              key={card.id || card.label}
              label={card.label}
              value={card.value}
              helperText={card.helperText}
              changeValue={card.changeValue}
              variant={card.variant}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default ParaplanningSummaryCards;
